import React, { useEffect, useState, useRef } from 'react';
import './App.css';

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    // Adjust viewport height for mobile Safari
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    return () => window.removeEventListener('resize', setViewportHeight);
  }, []);

  // On mount, fetch the initial conversation
  useEffect(() => {
    fetchInitialConversation();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const fetchInitialConversation = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [] }),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.messages) {
        const filtered = data.messages.filter(m => {
          // Filter out the initial user prompt that starts with "Help me study for IELTS"
          return !(m.role === 'user' && m.content.startsWith("Help me study for IELTS"));
        });
        setMessages(filtered);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMsg = { role: 'user', content: input.trim() };

    // Show user message immediately (optimistic UI)
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [userMsg] }),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.messages) {
        // Filter out the hidden initial prompt if it appears (shouldn't at this stage, but just to be safe)
        const filtered = data.messages.filter(m => {
          return !(m.role === 'user' && m.content.startsWith("Help me study for IELTS"));
        });
        setMessages(filtered);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const newChat = async () => {
    const confirmNewChat = window.confirm("Start a new chat?");
    if (!confirmNewChat) return;

    setMessages([]);
    setInput('');
    setLoading(true);
    try {
      await fetch('/api/new-chat', {
        method: 'POST',
        credentials: 'include'
      });
      // After clearing on the server, fetch the initial conversation again
      await fetchInitialConversation();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>ChatMentor (IELTS)</h1>
        <button onClick={newChat} disabled={loading} className="new-chat-button">New Chat</button>
      </header>
      <div className="chat-container">
        <div className="messages">
          {messages.map((m, i) => (
            <div key={i} className={`message ${m.role}`}>
              <div className="message-bubble">
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message assistant">
              <div className="message-bubble">Thinking...</div>
            </div>
          )}
          <div ref={bottomRef}></div>
        </div>
        <div className="input-area">
          <input
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading) {
                sendMessage();
              }
            }}
            disabled={loading}
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()}>Send</button>
        </div>
      </div>
    </div>
  );
}

export default App;

